import { FormArray, FormGroup } from '@angular/forms';
import { SelectOption } from '@common-modules/shared/model/shared/select-option';
import cloneDeep from 'clone-deep';
import DOMPurify from 'dompurify';
import deepEqual from 'fast-deep-equal/es6';
import { saveAs } from 'file-saver';
import isSvg from 'is-svg';
import * as $ from 'jquery';
import { Observable } from 'rxjs';
import UtmObj from 'utm-latlng';
import { v4 as uuidv4, validate as validateGuid } from 'uuid';
import { IElementSize } from '../model/element-size';

const cloneDeepFn =
  typeof cloneDeep === 'object' && typeof cloneDeep.default === 'function'
    ? cloneDeep.default
    : cloneDeep;

/**
 * Encapsulates some useful methods that are sometimes used outside dependency injection, so they cannot go inside a service.
 */

function serializedClone<T>(obj): T {
  const cloned = JSON.parse(JSON.stringify(obj));
  return cloned;
}

function clone<T>(obj: T, useInstanceClone = false): T {
  if (useInstanceClone) {
    // Fallback that specifies how to clone instance of classes.
    const instanceClone = globalUtilsHelper.serializedClone;
    return cloneDeepFn(obj, instanceClone);
  }
  return cloneDeepFn(obj);
}

function generateGuid(): string {
  return uuidv4();
}

function isNullUndefined(value): boolean {
  return value === null || typeof value === 'undefined';
}

/**
 * This method only works if it receives an array of objects.
 * For an array of arrays, use groupArraysByFn.
 */
function groupByFn(array: any[], fn: (item) => any): { [key: string]: any[] } {
  return array.reduce((accum, item) => {
    (accum[fn(item)] = accum[fn(item)] || []).push(item);
    return accum;
  }, {});
}

function groupByFnHashInternal<TItem, TGroup>(
  array: TItem[],
  fn: (item: TItem) => any,
  addNewGroup: (newItem: TItem) => TGroup,
  addToGroup: (accum: TGroup, newitem: TItem) => TGroup
): Map<any, TGroup> {
  const hash = new Map<string | number, TGroup>();
  array.forEach((item) => {
    const key = fn(item);
    if (hash.has(key)) {
      hash.set(key, addToGroup(hash.get(key), item));
    } else {
      hash.set(key, addNewGroup(item));
    }
  });
  return hash;
}

function groupByFnHash<T>(array: T[], fn: (item: T) => any): Map<any, T[]> {
  const result = groupByFnHashInternal<T, T[]>(
    array,
    fn,
    (item) => [item],
    (group, item) => group.concat(item)
  );
  return result;
}

function groupByFnHashSingle<T>(array: T[], fn: (item) => any): Map<any, T> {
  const result = groupByFnHashInternal<T, T>(
    array,
    fn,
    (item) => item,
    (group, item) => item
  );
  return result;
}

function exportURL(url, name: string) {
  saveAs(url, name);
}

function resizeImage(url, width, fileName, callback) {
  var sourceImage = new Image();

  sourceImage.onload = function () {
    var canvas = document.createElement('canvas'),
      ctx = canvas.getContext('2d'),
      oc = document.createElement('canvas'),
      octx = oc.getContext('2d');

    canvas.width = width; // destination canvas size
    canvas.height = (canvas.width * sourceImage.height) / sourceImage.width;

    var cur = {
      width: Math.floor(sourceImage.width * 0.5),
      height: Math.floor(sourceImage.height * 0.5),
    };

    oc.width = cur.width;
    oc.height = cur.height;

    octx.drawImage(sourceImage, 0, 0, cur.width, cur.height);

    while (cur.width * 0.5 > width) {
      cur = {
        width: Math.floor(cur.width * 0.5),
        height: Math.floor(cur.height * 0.5),
      };
      octx.drawImage(oc, 0, 0, cur.width * 2, cur.height * 2, 0, 0, cur.width, cur.height);
    }

    ctx.drawImage(oc, 0, 0, cur.width, cur.height, 0, 0, canvas.width, canvas.height);

    // Convert the canvas to a data URL in PNG format
    callback(canvas.toDataURL(), fileName);
  };

  sourceImage.src = url;
}

function getElementSize(element: HTMLElement): IElementSize {
  if (!element) {
    return null;
  }
  const size: IElementSize = {
    width: element.offsetWidth,
    height: element.offsetHeight,
  };

  return size;
}

function downloadFile(url, fileName: string) {
  let link = document.createElement('a');
  link.setAttribute('type', 'hidden');
  link.href = `${url}`;
  link.download = fileName;

  document.body.appendChild(link);

  link.click();
  link.remove();
}

function downloadBlobFile(blob: Blob, fileName: string): void {
  const url = window.URL.createObjectURL(blob);
  downloadFile(url, fileName);
}

function fileToBase64(file: File): Observable<string> {
  return new Observable((observer) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      const base64result = (reader.result as string).split(',')[1];

      observer.next(base64result);
      observer.complete();
    };
    reader.onerror = (error) => {
      observer.error(error);
    };
  });
}

function camelcase(str: string): string {
  return str
    .replace(/([a-z])([A-Z])/g, '$1-$2')
    .replace(/[\s_]+/g, '-')
    .toLowerCase();
}

function removeFilePath(title: string): string {
  if (!title) {
    return title;
  }

  if (title.includes(`\\`)) {
    const splitted = title.split(`\\`);
    return splitted[splitted.length - 1];
  }

  return title;
}

function convertLatLongToUtm(
  latitude: number,
  longitude: number
): { easting: number; northing: number; zoneNumber: number; zoneLetter: string } {
  const utm = new UtmObj();
  const precision = 5;
  const utmCoords = (utm as any).convertLatLngToUtm(latitude, longitude, precision);

  if (typeof utmCoords === 'string') {
    throw new Error(`Error converting coordinates: ${utmCoords}`);
  }

  return {
    easting: utmCoords.Easting,
    northing: utmCoords.Northing,
    zoneNumber: utmCoords.ZoneNumber,
    zoneLetter: utmCoords.ZoneLetter,
  };
}

/**
 * Convert numeric formats to the format that angular decimal pipe uses.
 */
function translateNumericFormat(format: string): string {
  if (!format) {
    return null;
  }
  let currentFormat = format;
  const matches = format.match(/^(0+).(0+)$/);
  if (matches && matches.length === 3) {
    const digits = matches[1].length;
    const decimals = matches[2].length;
    currentFormat = `${digits}.${decimals}-${decimals}`;
  }
  return currentFormat;
}

function getFirstUrlSegment(url: string): string | null {
  const urlParts = url.split('://');
  if (urlParts.length >= 2) {
    const restOfParts = urlParts[1].split('.');
    return restOfParts[0];
  }
  return null;
}

function mapToPlainObject(map: Map<any, any>): { [key: string | number]: any } {
  const obj = Array.from(map.entries()).reduce(
    (main, [key, value]) => ({ ...main, [key]: value }),
    {}
  );
  return obj;
}

function mapToPlainObjectRecursive(map: Map<any, any>): any {
  const obj = Array.from(map.entries()).reduce(
    (main, [key, value]) => ({
      ...main,
      [key]: value instanceof Map ? this.mapToPlainObject(value) : value,
    }),
    {}
  );
  return obj;
}

function queryStringToJson(queryString) {
  if (queryString.indexOf('?') > -1) {
    queryString = queryString.split('?')[1];
  }
  const pairs = queryString.split('&');
  const result = {};
  pairs.forEach(function (pair) {
    pair = pair.split('=');
    result[pair[0]] = decodeURIComponent(pair[1] || '');
  });
  return result;
}

function jsonToQueryString(object): string {
  return $.param(object);
}

function setUrlFragments(fragments: string): void {
  window.location.hash = fragments;
}

function prettifySvg(xml: string, tab = '\t', nl = '\n'): string {
  if (!xml) {
    return xml;
  }
  let formatted = '',
    indent = '';
  const nodes = xml.slice(1, -1).split(/>\s*</);
  if (nodes[0][0] == '?') formatted += '<' + nodes.shift() + '>' + nl;
  for (let i = 0; i < nodes.length; i++) {
    const node = nodes[i];
    if (node[0] == '/') indent = indent.slice(tab.length); // decrease indent
    formatted += indent + '<' + node + '>' + nl;
    if (node[0] != '/' && node[node.length - 1] != '/' && node.indexOf('</') == -1) indent += tab; // increase indent
  }
  return formatted;
}

/**
 * Handle the case in which numbers are returned with comma instead of dot.
 */
function commaNumericStringToNumber(str: string): number | null {
  if (!str) {
    return null;
  }

  const commaMatches = str.match(/,/);
  const dotMatches = str.match(/\./);
  let formatted = str;
  if (dotMatches?.length > 1) {
    formatted = formatted.replace(/\./, '');
  }
  if (commaMatches?.length > 1 || (commaMatches?.length === 1 && dotMatches?.length === 1)) {
    formatted = formatted.replace(/,/, '');
  }
  if (commaMatches?.length === 1) {
    formatted = str.replace(/,/, '.');
  }
  return +formatted;
}

function isSvgText(svgText: string): boolean {
  return !svgText ? false : isSvg(svgText);
}

function getDOMPurify() {
  const purify = DOMPurify(window);
  return purify;
}

function purifyHtml(dirty: string): string {
  const clean = getDOMPurify().sanitize(dirty, { USE_PROFILES: { html: true } });
  return clean;
}

function purifySvg(dirty: string): string {
  const clean = getDOMPurify().sanitize(dirty, { USE_PROFILES: { svg: true } });
  return clean;
}

function isValidJson(data: string): boolean {
  try {
    JSON.parse(data);
  } catch (e) {
    return false;
  }
  return true;
}

function isObject(obj): boolean {
  return typeof obj === 'object' && !Array.isArray(obj) && obj !== null;
}

function markAllAsTouched(group: FormGroup | FormArray): void {
  Object.keys(group.controls).forEach((key: string) => {
    const abstractControl = group.controls[key];

    if (abstractControl instanceof FormGroup || abstractControl instanceof FormArray) {
      markAllAsTouched(abstractControl);
    } else {
      abstractControl.markAsTouched();
    }
  });
}

function normalizeId(id): string {
  let strId;
  if (globalUtilsHelper.isObject(id)) {
    strId = JSON.stringify(id);
  } else {
    try {
      const parsed = JSON.parse(id);
      strId = JSON.stringify(parsed);
    } catch (error) {
      strId = String(id);
    }

    strId = String(id);
  }

  return strId?.toLowerCase();
}

/**
 * Include all the ancestors of the nodes. Only include each node once.
 */
function getAncestors<T>(
  selectedNodes: T[],
  allNodes: T[],
  getKey: (node: T) => string,
  getParentKey: (node: T) => string
): T[] {
  let results = [...selectedNodes];
  selectedNodes.forEach((selectedNode) => {
    results = results.concat(getNodeAncestors(selectedNode, allNodes, getKey, getParentKey));
  });
  const resultsUnique: T[] = [];
  const included: Map<string, boolean> = new Map();
  results.forEach((node) => {
    const nodeKey = getKey(node);
    if (!included.has(nodeKey)) {
      resultsUnique.push(node);
      included.set(nodeKey, true);
    }
  });
  return resultsUnique;
}

/**
 * Get all the ancestors nodes of a node.
 */
function getNodeAncestors<T>(
  node: T,
  allNodes: T[],
  getKey: (node: T) => string,
  getParentKey: (node: T) => string
): T[] {
  const ancestors = [];
  const parentNode = allNodes.find((item) => getKey(item) === getParentKey(node));
  if (parentNode) {
    getNodeAncestorsRecursive(parentNode, ancestors, allNodes, getKey, getParentKey);
  }
  return ancestors;
}

/**
 * Get all the ancestors nodes of a node. Helper method.
 */
function getNodeAncestorsRecursive<T>(
  node: T,
  ancestors: T[],
  allNodes: T[],
  getKey: (node: T) => string,
  getParentKey: (node: T) => string
): void {
  ancestors.unshift(node);
  const parentNode = allNodes.find((item) => getKey(item) === getParentKey(node));
  if (parentNode) {
    getNodeAncestorsRecursive(parentNode, ancestors, allNodes, getKey, getParentKey);
  }
}

function hashToSelectOptions(mapping: Map<string, string>): SelectOption<string>[] {
  return Array.from(mapping.entries()).map(
    (entry) =>
      new SelectOption<string>({
        value: entry[0],
        label: entry[1] ?? entry[0],
      })
  );
}

function getPathFromSvg(svgText: string): string {
  if (!svgText) {
    return null;
  }

  const parser = new DOMParser();
  const svgDoc = parser.parseFromString(svgText, 'image/svg+xml');

  const paths = svgDoc.querySelectorAll('path');

  if (paths.length) {
    const result = paths[0].getAttribute('d');
    return result;
  }

  return null;
}

const getFileSize = (imgData: string): Observable<IElementSize> => {
  return new Observable<IElementSize>((o) => {
    const img = new Image();

    img.onload = function () {
      o.next({ width: img.width, height: img.height } as IElementSize);
      o.complete();
    };

    img.src = imgData;
  });
};

/**
 * Derive height to try to keep acceptable ratio.
 */
const scaleImgHeight = (imgSize: IElementSize, fixedWidth: number): IElementSize => {
  const sizeRatio = imgSize.width / imgSize.height;
  const height = fixedWidth / sizeRatio;

  return {
    width: fixedWidth,
    height,
  };
};

const camelize = (str) => {
  let arr = str.split('-');
  let capital = arr.map((item, index) => index ? item.charAt(0).toUpperCase() + item.slice(1).toLowerCase() : item.toLowerCase());
  const capitalString = capital.join("");
  return capitalString;
}

function applySvgColor(svgText, color) {
  // Parse the SVG string into a DOM element
  const parser = new DOMParser();
  const svgDoc = parser.parseFromString(svgText, 'image/svg+xml');
  const svgElement = svgDoc.documentElement;

  // Check if parsing was successful
  if (svgElement.tagName !== 'svg') {
    throw new Error('Invalid SVG format');
  }

  // Remove any existing fill and stroke attributes from the root SVG
  svgElement.removeAttribute('fill');
  //svgElement.removeAttribute('stroke');

  // Find all elements that can be colored
  const colorableElements = svgElement.querySelectorAll('path, circle, rect, ellipse, line, polyline, polygon, text');

  colorableElements.forEach(element => {
    // Only modify elements that don't have fill="none" or stroke="none"
    const currentFill = element.getAttribute('fill');
    //const currentStroke = element.getAttribute('stroke');

    // If element has fill and it's not "none", apply the color
    if (currentFill !== 'none' && currentFill) {
      element.setAttribute('fill', color);
    }

    // If element has neither fill nor stroke, default to fill
    if (!currentFill) {
      element.setAttribute('fill', color);
    }
  });

  // Convert back to string
  const serializer = new XMLSerializer();
  return serializer.serializeToString(svgElement);
}

// Sort the target array based on the order of the reference array
function sortByReferenceOrder(referenceArray, targetArray, getKey: (item) => any) {
  // Create a map of key to index from the reference array
  const orderMap = new Map();
  referenceArray.forEach((item, index) => {
    orderMap.set(getKey(item), index);
  });
  
  // Sort the target array based on the reference order
  return targetArray.sort((a, b) => {
    const indexA = orderMap.get(getKey(a));
    const indexB = orderMap.get(getKey(b));
    
    // Handle cases where keys might not exist in reference array
    if (indexA === undefined && indexB === undefined) return 0;
    if (indexA === undefined) return 1; // Put undefined keys at the end
    if (indexB === undefined) return -1;
    
    return indexA - indexB;
  });
}

export const globalUtilsHelper = {
  serializedClone,
  clone,
  generateGuid,
  validateGuid,
  isNullUndefined,
  exportURL,
  resizeImage,
  groupByFn,
  groupByFnHash,
  groupByFnHashSingle,
  getElementSize,
  downloadFile,
  fileToBase64,
  camelcase,
  deepEqual,
  downloadBlobFile,
  removeFilePath,
  translateNumericFormat,
  getFirstUrlSegment,
  jsonToQueryString,
  setUrlFragments,
  mapToPlainObject,
  mapToPlainObjectRecursive,
  queryStringToJson,
  prettifySvg,
  isSvgText,
  commaNumericStringToNumber,
  purifyHtml,
  isValidJson,
  isObject,
  markAllAsTouched,
  normalizeId,
  getAncestors,
  purifySvg,
  hashToSelectOptions,
  getPathFromSvg,
  convertLatLongToUtm,
  getFileSize,
  scaleImgHeight,
  camelize,
  applySvgColor,
  sortByReferenceOrder 
};
